/**
 * Same rules as Kore WorkflowContextExpression: dotted path or + - * / ( ).
 * Identifiers resolved with Utils.getByPath(source, id).
 */

import Utils from './Utils';

/** @param {string} expr */
export function workflowContextExpressionLooksLikeExpression(expr) {
    const t = String(expr || '').trim();
    if (!t) {
        return false;
    }
    if (/[+*/()]/.test(t)) {
        return true;
    }
    if (!t.includes('-')) {
        return false;
    }
    return !/^[a-zA-Z_][a-zA-Z0-9_.]*$/.test(t);
}

class ExprParser {
    /**
     * @param {object} source
     * @param {string} s
     */
    constructor(source, s) {
        this.source = source;
        this.s = s;
        this.i = 0;
        this.n = s.length;
    }

    peek() {
        return this.i < this.n ? this.s[this.i] : null;
    }

    eat() {
        this.i += 1;
    }

    skipSpace() {
        while (this.i < this.n && /\s/.test(this.s[this.i])) {
            this.i += 1;
        }
    }

    readNumber() {
        const start = this.i;
        while (this.i < this.n && /\d/.test(this.s[this.i])) {
            this.i += 1;
        }
        if (this.i < this.n && this.s[this.i] === '.') {
            this.i += 1;
            while (this.i < this.n && /\d/.test(this.s[this.i])) {
                this.i += 1;
            }
        }
        const str = this.s.slice(start, this.i);
        if (!str || str === '.') {
            throw new Error('bad number');
        }
        return str.includes('.') ? parseFloat(str) : parseInt(str, 10);
    }

    readIdentifier() {
        const start = this.i;
        this.i += 1;
        while (this.i < this.n) {
            const ch = this.s[this.i];
            if (/[a-zA-Z0-9_.]/.test(ch)) {
                this.i += 1;
            } else {
                break;
            }
        }
        return this.s.slice(start, this.i);
    }

    parseFactor() {
        this.skipSpace();
        const c = this.peek();
        if (c === '+') {
            this.eat();
            return this.parseFactor();
        }
        if (c === '-') {
            this.eat();
            return unaryMinus(this.parseFactor());
        }
        if (c === '(') {
            this.eat();
            const v = this.parseExpr();
            this.skipSpace();
            if (this.peek() !== ')') {
                throw new Error('expected )');
            }
            this.eat();
            return v;
        }
        if (c != null && (/\d/.test(c) || c === '.')) {
            return this.readNumber();
        }
        if (c != null && (/[a-zA-Z_]/.test(c))) {
            const id = this.readIdentifier();
            return getPath(this.source, id);
        }
        throw new Error('unexpected token');
    }

    parseTerm() {
        let v = this.parseFactor();
        while (true) {
            this.skipSpace();
            const c = this.peek();
            if (c === '*') {
                this.eat();
                v = binMul(v, this.parseFactor());
            } else if (c === '/') {
                this.eat();
                v = binDiv(v, this.parseFactor());
            } else {
                break;
            }
        }
        return v;
    }

    parseExpr() {
        let v = this.parseTerm();
        while (true) {
            this.skipSpace();
            const c = this.peek();
            if (c === '+') {
                this.eat();
                v = binPlus(v, this.parseTerm());
            } else if (c === '-') {
                this.eat();
                v = binMinus(v, this.parseTerm());
            } else {
                break;
            }
        }
        return v;
    }
}

class IdParser {
    /**
     * @param {string} s
     */
    constructor(s) {
        this.s = s;
        this.i = 0;
        this.n = s.length;
        /** @type {string[]} */
        this.ids = [];
    }

    peek() {
        return this.i < this.n ? this.s[this.i] : null;
    }

    eat() {
        this.i += 1;
    }

    skipSpace() {
        while (this.i < this.n && /\s/.test(this.s[this.i])) {
            this.i += 1;
        }
    }

    readNumber() {
        const start = this.i;
        while (this.i < this.n && /\d/.test(this.s[this.i])) {
            this.i += 1;
        }
        if (this.i < this.n && this.s[this.i] === '.') {
            this.i += 1;
            while (this.i < this.n && /\d/.test(this.s[this.i])) {
                this.i += 1;
            }
        }
        const str = this.s.slice(start, this.i);
        if (!str || str === '.') {
            throw new Error('bad number');
        }
    }

    readIdentifier() {
        const start = this.i;
        this.i += 1;
        while (this.i < this.n) {
            const ch = this.s[this.i];
            if (/[a-zA-Z0-9_.]/.test(ch)) {
                this.i += 1;
            } else {
                break;
            }
        }
        return this.s.slice(start, this.i);
    }

    parseFactor() {
        this.skipSpace();
        const c = this.peek();
        if (c === '+' || c === '-') {
            this.eat();
            this.parseFactor();
            return;
        }
        if (c === '(') {
            this.eat();
            this.parseExpr();
            this.skipSpace();
            if (this.peek() !== ')') {
                throw new Error('expected )');
            }
            this.eat();
            return;
        }
        if (c != null && (/\d/.test(c) || c === '.')) {
            this.readNumber();
            return;
        }
        if (c != null && /[a-zA-Z_]/.test(c)) {
            this.ids.push(this.readIdentifier());
            return;
        }
        throw new Error('unexpected token');
    }

    parseTerm() {
        this.parseFactor();
        while (true) {
            this.skipSpace();
            const c = this.peek();
            if (c === '*' || c === '/') {
                this.eat();
                this.parseFactor();
            } else {
                break;
            }
        }
    }

    parseExpr() {
        this.parseTerm();
        while (true) {
            this.skipSpace();
            const c = this.peek();
            if (c === '+' || c === '-') {
                this.eat();
                this.parseTerm();
            } else {
                break;
            }
        }
    }
}

/**
 * @param {object} source
 * @param {string} path
 */
function getPath(source, path) {
    return Utils.getByPath(source, path);
}

function unaryMinus(a) {
    if (a == null) {
        return null;
    }
    if (typeof a === 'number' && Number.isFinite(a)) {
        return -a;
    }
    const n = Number(a);
    if (Number.isFinite(n)) {
        return -n;
    }
    return null;
}

function binPlus(a, b) {
    if (a == null || b == null) {
        return null;
    }
    if (typeof a === 'number' && typeof b === 'number' && Number.isFinite(a) && Number.isFinite(b)) {
        return a + b;
    }
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) {
        return na + nb;
    }
    return String(a) + String(b);
}

function binMinus(a, b) {
    if (a == null || b == null) {
        return null;
    }
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) {
        return na - nb;
    }
    return null;
}

function binMul(a, b) {
    if (a == null || b == null) {
        return null;
    }
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) {
        return na * nb;
    }
    return null;
}

function binDiv(a, b) {
    if (a == null || b == null) {
        return null;
    }
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isFinite(na) || !Number.isFinite(nb) || nb === 0) {
        return null;
    }
    return na / nb;
}

/**
 * Live syntax check for custom context path / arithmetic expression fields.
 *
 * @param {string} rawExpr
 * @returns {{ level: 'neutral' }|{ level: 'ok', kind: 'path' }|{ level: 'ok', kind: 'expression' }|{ level: 'error', key: 'syntax_invalid'|'syntax_trailing' }}
 */
export function getWorkflowContextExpressionSyntaxResult(rawExpr) {
    const raw = String(rawExpr ?? '');
    const t = raw.trim();
    if (!t) {
        return { level: 'neutral' };
    }
    if (!workflowContextExpressionLooksLikeExpression(t)) {
        return { level: 'ok', kind: 'path' };
    }
    try {
        const p = new IdParser(t);
        p.parseExpr();
        p.skipSpace();
        if (p.i < p.n) {
            return { level: 'error', key: 'syntax_trailing' };
        }
        return { level: 'ok', kind: 'expression' };
    } catch (_e) {
        return { level: 'error', key: 'syntax_invalid' };
    }
}

/**
 * @param {Set<string>|string[]|null|undefined} knownPaths
 * @returns {Set<string>|null}
 */
function normalize_known_paths_set(knownPaths) {
    if (knownPaths == null) {
        return null;
    }
    if (knownPaths instanceof Set) {
        return knownPaths.size > 0 ? knownPaths : null;
    }
    if (Array.isArray(knownPaths) && knownPaths.length > 0) {
        return new Set(knownPaths.map((p) => String(p)));
    }
    return null;
}

/**
 * Identifiers referenced in the expression (or a single path). Empty when syntax is invalid for expressions.
 *
 * @param {string} rawExpr
 * @returns {string[]}
 */
export function getWorkflowContextExpressionIdentifiers(rawExpr) {
    const t = String(rawExpr ?? '').trim();
    if (!t) {
        return [];
    }
    if (!workflowContextExpressionLooksLikeExpression(t)) {
        return [t];
    }
    const parsed = parseIdentifiers(t);
    if (!parsed || parsed.length === 0) {
        return [];
    }
    return [...new Set(parsed)];
}

/**
 * Paths used in the expression that are not in the workflow’s known context path catalog (e.g. typo `child_coun` vs `child_count`).
 *
 * @param {string} rawExpr
 * @param {Set<string>|string[]|null|undefined} knownPaths
 * @returns {string[]}
 */
export function getUnknownWorkflowContextIdentifiers(rawExpr, knownPaths) {
    const set = normalize_known_paths_set(knownPaths);
    if (!set) {
        return [];
    }
    const ids = getWorkflowContextExpressionIdentifiers(rawExpr);
    if (ids.length === 0) {
        return [];
    }
    return ids.filter((id) => !set.has(id));
}

/**
 * @param {string} expr
 * @returns {string[]|null}
 */
function parseIdentifiers(expr) {
    try {
        const p = new IdParser(String(expr).trim());
        p.parseExpr();
        p.skipSpace();
        if (p.i < p.n) {
            return null;
        }
        return p.ids;
    } catch (_e) {
        return null;
    }
}

/**
 * @param {object} source
 * @param {string} expr
 * @returns {unknown}
 */
export function evaluateWorkflowContextExpression(source, expr) {
    const t = String(expr || '').trim();
    if (!t) {
        return undefined;
    }
    if (!workflowContextExpressionLooksLikeExpression(t)) {
        return getPath(source, t);
    }
    try {
        const p = new ExprParser(source, t);
        const v = p.parseExpr();
        p.skipSpace();
        if (p.i < p.n) {
            return undefined;
        }
        return v;
    } catch (_e) {
        return undefined;
    }
}

const ABSENT = '__absent__';

/**
 * @param {object} source
 * @param {string} expr
 */
export function allReferencedPathsExistInSource(source, expr) {
    const t = String(expr || '').trim();
    if (!t) {
        return false;
    }
    const ids = workflowContextExpressionLooksLikeExpression(t)
        ? parseIdentifiers(t)
        : [t];
    if (!ids || ids.length === 0) {
        return false;
    }
    return ids.every((id) => {
        const v = getPath(source, id);
        return v !== undefined;
    });
}

/**
 * @param {object} source
 * @param {string} expr
 */
export function conditionLhsForWorkflow(source, expr) {
    const t = String(expr || '').trim();
    if (!workflowContextExpressionLooksLikeExpression(t)) {
        const v = Utils.getByPath(source, t);
        return v !== undefined ? v : ABSENT;
    }
    const ev = evaluateWorkflowContextExpression(source, t);
    return ev == null ? ABSENT : ev;
}
